import React, { useEffect, useRef, useState } from 'react';

/**
 * MapControlsPanel — the right overlay panel from the Stitch design:
 * location search, 2D/3D toggle, map style picker, zone filter,
 * and a "N of M selected · LIVE DATA" footer.
 */

const CAMERA_OPTIONS = [
    { id: 'first_person', label: '1st Person' },
    { id: 'second_person', label: '2nd Person' },
    { id: 'third_person', label: '3rd Person' },
];

function Icon({ name, size = 20, color, fill = false, style = {} }) {
    return (
        <span
            className="material-symbols-outlined"
            style={{ fontSize: size, color, fontVariationSettings: fill ? "'FILL' 1" : "'FILL' 0", ...style }}
        >
            {name}
        </span>
    );
}

function SectionButton({ open, onClick, icon, label, active = false }) {
    return (
        <button
            onClick={onClick}
            style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 12,
                background: active ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'color-mix(in srgb, var(--bgTertiary) 20%, transparent)',
                border: active ? '1px solid color-mix(in srgb, var(--accent) 50%, transparent)' : '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)',
                borderRadius: open ? '8px 8px 0 0' : 8,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: active ? 'var(--accent)' : 'var(--textPrimary)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Icon name={icon} color="var(--accent)" />
                <span className="stitch-label" style={{ fontSize: 12 }}>{label}</span>
            </div>
            <Icon name={open ? 'expand_less' : 'expand_more'} color={active ? 'var(--accent)' : 'var(--stitch-outline)'} />
        </button>
    );
}

function MapControlsPanel({
    // projection
    mapMode, setMapMode, canRender3D = true,
    // 3D options
    cameraMode, setCameraMode, show3DBuildings, setShow3DBuildings,
    // styles
    stylesCatalog = {}, currentStyle, onStyleChange,
    // zones
    availableZones = [], selectedZones = [], onZoneToggle, onSelectAll,
    // phone: fills the layers sheet instead of the 320px desktop column
    fluid = false,
}) {
    const [styleOpen, setStyleOpen] = useState(false);
    // Collapsed by default — six zone rows open on load push every other control
    // off the panel, and nothing is filtered until the operator picks something.
    const [zonesOpen, setZonesOpen] = useState(false);
    const allSelected = selectedZones.length === availableZones.length && availableZones.length > 0;

    // The voice agent grows upward from the bottom-right and used to climb
    // straight into this panel once the transcript got long. Publish how far
    // down the right column this panel reaches so it can cap itself. Sections
    // expand and collapse, so measure rather than guess.
    const panelRef = useRef(null);
    useEffect(() => {
        const el = panelRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const publish = () => document.documentElement.style.setProperty(
            '--map-controls-height', `${Math.round(el.getBoundingClientRect().height)}px`,
        );
        publish();
        const observer = new ResizeObserver(publish);
        observer.observe(el);
        return () => {
            observer.disconnect();
            document.documentElement.style.setProperty('--map-controls-height', '0px');
        };
    }, []);

    return (
        <div
            ref={panelRef}
            className="glass-panel"
            style={{
                width: fluid ? '100%' : '100%',
                maxWidth: fluid ? undefined : 320,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                pointerEvents: 'auto',
                maxHeight: '100%',
            }}
        >
            {/* Header (the phone sheet brings its own) */}
            {!fluid && (
            <div style={{ flexShrink: 0, padding: 16, background: 'color-mix(in srgb, var(--bgPrimary) 20%, transparent)', borderBottom: '1px solid color-mix(in srgb, var(--borderSecondary) 30%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--accent)', margin: 0, fontFamily: 'var(--font-body)' }}>
                    Map Controls
                </h2>
                <Icon name="tune" color="var(--stitch-outline)" />
            </div>
            )}

            {/* Body */}
            <div className="custom-scrollbar" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', minHeight: 0 }}>
                {/* 2D / 3D toggle */}
                <div style={{ display: 'flex', background: 'color-mix(in srgb, var(--bgTertiary) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)', borderRadius: 8, padding: 4 }}>
                    {['2d', '3d'].map((m) => {
                        const isActive = mapMode === m;
                        const disabled = m === '3d' && !canRender3D;
                        return (
                            <button
                                key={m}
                                onClick={() => !disabled && setMapMode(m)}
                                disabled={disabled}
                                style={{
                                    flex: 1,
                                    padding: '8px 16px',
                                    borderRadius: 6,
                                    border: 'none',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    background: isActive ? 'var(--accent)' : 'transparent',
                                    color: isActive ? 'var(--stitch-on-primary)' : (disabled ? 'var(--textMuted)' : 'var(--textSecondary)'),
                                    fontFamily: 'var(--font-label)',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    boxShadow: isActive ? '0 4px 14px color-mix(in srgb, var(--accent) 35%, transparent)' : 'none',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {m.toUpperCase()} VIEW
                            </button>
                        );
                    })}
                </div>

                {/* 3D options */}
                {mapMode === '3d' && (
                    <div style={{ background: 'color-mix(in srgb, var(--bgPrimary) 50%, transparent)', border: '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <span className="stitch-label" style={{ fontSize: 10, color: 'var(--stitch-outline)', textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                            Camera
                        </span>
                        <div style={{ display: 'flex', gap: 4 }}>
                            {CAMERA_OPTIONS.map((o) => (
                                <button
                                    key={o.id}
                                    onClick={() => setCameraMode(o.id)}
                                    style={{
                                        flex: 1,
                                        padding: '6px 4px',
                                        borderRadius: 6,
                                        border: cameraMode === o.id ? '1px solid color-mix(in srgb, var(--accent) 50%, transparent)' : '1px solid transparent',
                                        background: cameraMode === o.id ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'transparent',
                                        color: cameraMode === o.id ? 'var(--accent)' : 'var(--textSecondary)',
                                        fontFamily: 'var(--font-label)',
                                        fontSize: 10,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                        <label className="stitch-label" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--textPrimary)', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={show3DBuildings}
                                onChange={(e) => setShow3DBuildings(e.target.checked)}
                                style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                            />
                            3D Buildings
                        </label>
                    </div>
                )}

                {/* Map Style */}
                <div>
                    <SectionButton
                        open={styleOpen}
                        onClick={() => setStyleOpen((v) => !v)}
                        icon="layers"
                        label="Map Style"
                    />
                    {styleOpen && (
                        <div style={{ background: 'color-mix(in srgb, var(--bgPrimary) 60%, transparent)', border: '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                            {Object.entries(stylesCatalog).map(([key, cfg]) => {
                                const isActive = currentStyle === key;
                                return (
                                    <div
                                        key={key}
                                        onClick={() => onStyleChange(key)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 12,
                                            cursor: 'pointer',
                                            background: isActive ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'transparent',
                                            borderLeft: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                                            transition: 'background 0.15s ease',
                                        }}
                                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'color-mix(in srgb, var(--bgTertiary) 25%, transparent)'; }}
                                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div
                                                style={{
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: 3,
                                                    border: isActive ? 'none' : '1px solid var(--stitch-outline-variant)',
                                                    background: isActive ? 'var(--accent)' : 'transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {isActive && <div style={{ width: 6, height: 6, background: 'var(--stitch-on-primary)' }} />}
                                            </div>
                                            <span className="stitch-label" style={{ fontSize: 12, color: isActive ? 'var(--accent)' : 'var(--textSecondary)' }}>
                                                {cfg.label}
                                            </span>
                                        </div>
                                        {cfg.free && (
                                            <span style={{ fontSize: 8, fontWeight: 700, background: 'rgba(22, 163, 74, 0.1)', color: '#22c55e', padding: '2px 6px', borderRadius: 4, border: '1px solid rgba(34, 197, 94, 0.2)', fontFamily: 'var(--font-label)' }}>
                                                FREE
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Zone Filter */}
                {availableZones.length > 0 && (
                    <div>
                        <SectionButton
                            open={zonesOpen}
                            onClick={() => setZonesOpen((v) => !v)}
                            icon="filter_alt"
                            label="Zone Filter"
                            active
                        />
                        {zonesOpen && (
                            <div style={{ background: 'color-mix(in srgb, var(--bgPrimary) 60%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                                <button
                                    onClick={onSelectAll}
                                    style={{
                                        width: '100%',
                                        padding: 12,
                                        background: 'color-mix(in srgb, var(--bgTertiary) 20%, transparent)',
                                        border: 'none',
                                        borderBottom: '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)',
                                        color: 'var(--textSecondary)',
                                        fontFamily: 'var(--font-label)',
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.14em',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--bgTertiary) 40%, transparent)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--bgTertiary) 20%, transparent)'; }}
                                >
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                </button>
                                <div className="custom-scrollbar" style={{ maxHeight: 300, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {availableZones.map((zone) => {
                                        const checked = selectedZones.includes(zone);
                                        return (
                                            <div
                                                key={zone}
                                                onClick={() => onZoneToggle(zone)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    padding: 12,
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                    background: checked
                                                        ? 'linear-gradient(90deg, color-mix(in srgb, var(--accent) 85%, transparent), color-mix(in srgb, var(--accent) 30%, transparent))'
                                                        : 'color-mix(in srgb, var(--bgTertiary) 25%, transparent)',
                                                    color: checked ? 'var(--stitch-on-primary)' : 'var(--textSecondary)',
                                                    boxShadow: checked ? '0 4px 14px color-mix(in srgb, var(--accent) 25%, transparent)' : 'none',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <Icon
                                                    name={checked ? 'check_box' : 'check_box_outline_blank'}
                                                    size={20}
                                                    color={checked ? 'var(--stitch-on-primary)' : 'var(--stitch-outline)'}
                                                />
                                                <span className="stitch-label" style={{ fontSize: 12, fontWeight: checked ? 600 : 400 }}>
                                                    {zone}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div style={{ flexShrink: 0, padding: 16, borderTop: '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="stitch-label" style={{ fontSize: 10, color: 'var(--stitch-outline)' }}>
                    {selectedZones.length} of {availableZones.length} selected
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div className="stitch-pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />
                    <span className="stitch-label" style={{ fontSize: 10, color: 'var(--accent)' }}>LIVE DATA</span>
                </div>
            </div>
        </div>
    );
}

export default MapControlsPanel;
